import {
  DateRange,
}                              from 'rewire-common';
import {
  arraySearch,
  field,
  Form,
  IFormContext,
  validator
}                              from 'rewire-ui';
import {
  AuditLogEntryType,
  AuditLogEntryTypes,
  AuditLogEntryOperation,
  AuditLogEntryOperations,
  _auditLogEntryTypeMap,
  FieldFilter,
  IEmployee,
}                              from '../DataTypes';
import { getNameSearcher }     from '../utility/Searchers';
import FilterDialogModel       from '../utility/FilterDialogModel';
import auditLogListModel, {
  IAuditLogFilters,
}                              from './AuditLogListModel';
import { capitalCase }         from 'change-case';

const _auditLogFormFieldToFilterMap: Record<string, string> = {
  'types'          : 'types',
  'operations'     : 'operations',
  'createdBy'      : 'createdBy',
  'createdStart'   : 'createdDate',
  'createdEnd'     : 'createdDate',
  'effectiveStart' : 'effective',
  'effectiveEnd'   : 'effective',
  'employees'      : 'employeeId',
};

export class AuditLogFilterDialogModel extends FilterDialogModel {
  public constructor() {
    super('Filter By');
  }

  protected async _preload(_filters?: IAuditLogFilters) {
    const builtInFilterKeys           = Object.keys(auditLogListModel.builtInFilters);
    const builtInEntityDataFilterKeys = auditLogListModel.builtInEntityDataFilters.map(edf => edf.field);
    for (const field of this.form.fields) {
      field.visible = !builtInFilterKeys.some(k => k === _auditLogFormFieldToFilterMap[field.name]) && !builtInEntityDataFilterKeys.some(k => k === _auditLogFormFieldToFilterMap[field.name]);
    }
  }

  protected async _save(obj: ObjectType): Promise<boolean> {
    auditLogListModel.setAllFilters(obj.filters, obj.entityDataFilters);
    return true;
  }

  protected mapToForm(obj: ObjectType): ObjectType {
    const filters           = obj.filters;
    const entityDataFilters = obj.entityDataFilters;
    const filterFormFieldValues = {
      types: filters?.types,
      operations: filters?.operations,
      createdBy: filters?.createdBy,
      createdDateStart: filters?.createdDate?.start,
      createdDateEnd: filters?.createdDate?.end,
      effectiveStart: filters?.effective?.start,
      effectiveEnd: filters?.effective?.end,
      employees: entityDataFilters?.employees,
    } as ObjectType;
    return super.mapToForm(filterFormFieldValues);
  }

  protected mapToSave(): ObjectType {
    const newFilters = super.mapToSave();
    if (newFilters['createdDateStart'] && newFilters['createdDateEnd']) {
      newFilters['createdDate'] = new DateRange(newFilters['createdDateStart'], newFilters['createdDateEnd']);
      delete newFilters['createdDateStart'];
      delete newFilters['createdDateEnd'];
    }
    if (newFilters['effectiveStart'] && newFilters['effectiveEnd']) {
      newFilters['effective'] = new DateRange(newFilters['effectiveStart'], newFilters['effectiveEnd']);
      delete newFilters['effectiveStart'];
      delete newFilters['effectiveEnd'];
    }
    const newEntityDataFilters = [] as FieldFilter[];
    if (newFilters.employees?.length) {
      newEntityDataFilters.push({field: 'employeeId', in: newFilters.employees.map((e: IEmployee) => e.id)});
      delete newFilters.employees;
    }
    return {filters: newFilters, entityDataFilters: newEntityDataFilters};
  }

  protected _createForm(): Form {
    const typeSearcher      = arraySearch(Object.values(AuditLogEntryTypes),      (item?: AuditLogEntryType)      => (item && !Array.isArray(item) && _auditLogEntryTypeMap[item]) || '');
    const operationSearcher = arraySearch(Object.values(AuditLogEntryOperations), (item?: AuditLogEntryOperation) => (item && !Array.isArray(item) && capitalCase(item)) || '');
    const userSearcher      = getNameSearcher('users'); // add system?
    const employeeSearcher  = getNameSearcher('employees');

    return Form.create((form: IFormContext) => ({
      types:            form.multiselectautocomplete(typeSearcher, {chipLimit: 4})     .label('Type')              .placeholder('select Type(s)'),
      operations:       form.multiselectautocomplete(operationSearcher, {chipLimit: 5}).label('Operation')         .placeholder('select Operation(s)'),
      createdBy:        form.multiselectautocomplete(userSearcher, {chipLimit: 3})     .label('Created By')        .placeholder('select User(s)'),
      createdDateStart: form.date()                                                    .label('Created Date Start').placeholder('select Created Date Start').validators(validator('requiredWhenOthersAreNotNull', field('createdDateEnd'))),
      createdDateEnd:   form.date()                                                    .label('Created Date End')  .placeholder('select Created Date End')  .validators(validator('requiredWhenOthersAreNotNull', field('createdDateStart')), validator('>=', field('createdDateStart'))),
      effectiveStart:   form.date()                                                    .label('Effective Start')   .placeholder('select Effective Start')   .validators(validator('requiredWhenOthersAreNotNull', field('effectiveEnd'))),
      effectiveEnd:     form.date()                                                    .label('Effective End')     .placeholder('select Effective End')     .validators(validator('requiredWhenOthersAreNotNull', field('effectiveStart')), validator('>=', field('effectiveStart'))),
      employees:        form.multiselectautocomplete(employeeSearcher, {chipLimit: 3}) .label('Employees')         .placeholder('select Employee(s)'),
    }), {}, {});
  }
}

const auditLogFilterDialogModel = new AuditLogFilterDialogModel();
export default auditLogFilterDialogModel;
